import {
  AlertTriangle,
  CalendarDays,
  CheckCircle,
  Clock,
  Eye,
  FileDown,
  FileText,
  Hash,
  HelpCircle,
  Loader,
  MapPin,
  Package,
  Phone,
  RefreshCcw,
  Route,
  Search,
  ShieldAlert,
  ShoppingBag,
  ThumbsDown,
  Truck,
  User,
  XCircle,
} from "lucide-react";

import LoadingScreen from "../Utils/LoadingScreen";
import TablePagination from "../Utils/TablePagination";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";

const TaskTab = ({
  tasks,
  current,
  filtered,

  loading,
  globalSearchLoading,

  fromDate,
  setFromDate,

  toDate,
  setToDate,

  statusFilter,
  setStatusFilter,

  search,
  setSearch,

  fetchTasks,
  setTasks,

  pageInfo,
  setPageInfo,

  handleApplyFilters,
  handleTaskTabGlobalSearch,

  currentPage,
  setCurrentPage,

  perPage,

  handlePreviousPage,
  handleNextPage,

  setSelectedTask,
  setAssignmentLogs,
  setStatusChangeLogs,
  setShowLogs,
}) => {
  // =====================================================
  // STATUS BADGE
  // =====================================================

  const renderStatusBadge = (status) => {
    const statusMap = {
      unassigned: {
        color: "bg-blue-100 text-blue-700",
        icon: <HelpCircle className="w-4 h-4 mr-1" />,
        label: "Unassigned",
      },

      assigned: {
        color: "bg-yellow-100 text-yellow-800",
        icon: <Clock className="w-4 h-4 mr-1" />,
        label: "Assigned",
      },

      started: {
        color: "bg-amber-100 text-amber-800",
        icon: <Loader className="w-4 h-4 mr-1 animate-spin" />,
        label: "Started",
      },

      inprogress: {
        color: "bg-indigo-100 text-indigo-700",
        icon: <Loader className="w-4 h-4 mr-1 animate-spin" />,
        label: "In Progress",
      },

      "food is ready": {
        color: "bg-purple-100 text-purple-700",
        icon: <Truck className="w-4 h-4 mr-1" />,
        label: "Food Ready",
      },

      "picked up": {
        color: "bg-orange-100 text-orange-700",
        icon: <ShoppingBag className="w-4 h-4 mr-1" />,
        label: "Picked Up",
      },

      acknowledged: {
        color: "bg-teal-100 text-teal-700",
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: "Acknowledged",
      },

      successful: {
        color: "bg-green-100 text-green-700",
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: "Successful",
      },

      failed: {
        color: "bg-red-100 text-red-700",
        icon: <XCircle className="w-4 h-4 mr-1" />,
        label: "Failed",
      },

      declined: {
        color: "bg-pink-100 text-pink-700",
        icon: <ThumbsDown className="w-4 h-4 mr-1" />,
        label: "Declined",
      },

      cancelled: {
        color: "bg-gray-200 text-gray-700",
        icon: <AlertTriangle className="w-4 h-4 mr-1" />,
        label: "Cancelled",
      },
    };

    const { color, icon, label } = statusMap[status] || {
      color: "bg-slate-100 text-slate-700",

      icon: <HelpCircle className="w-4 h-4 mr-1" />,

      label: status,
    };

    return (
      <span
        className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}
        style={{
          minWidth: "130px",
        }}
      >
        {icon}

        <span className="truncate">{label}</span>
      </span>
    );
  };

  // =====================================================
  // DATE TIME
  // =====================================================

  const formatDateTime = (value) => {
    if (!value) return "N/A";

    try {
      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  // =====================================================
  // INFO BADGE
  // =====================================================

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-700",

      blue: "bg-blue-100 text-blue-700",

      green: "bg-green-100 text-green-700",

      orange: "bg-orange-100 text-orange-700",

      red: "bg-red-100 text-red-700",

      purple: "bg-purple-100 text-purple-700",

      yellow: "bg-yellow-100 text-yellow-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };

  // =====================================================
  // SHORT ADDRESS
  // =====================================================

  const getShortAddress = (address, length = 52) => {
    if (!address) return "N/A";

    if (address.length <= length) {
      return address;
    }

    return `${address.slice(0, length)}...`;
  };

  return (
    <>
      {/* =================================================
          FILTERS
      ================================================= */}

      <div className="shrink-0">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          {/* FROM */}

          <div>
            <label className="text-xs font-medium block mb-1">From</label>

            <input
              type="date"
              className="border rounded px-2 py-1 text-xs w-[120px]"
              value={fromDate}
              max={toDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          {/* TO */}

          <div>
            <label className="text-xs font-medium block mb-1">To</label>

            <input
              type="date"
              className="border rounded px-2 py-1 text-xs w-[120px]"
              value={toDate}
              min={fromDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>

          {/* APPLY */}

          <button
            type="button"
            onClick={handleApplyFilters}
            className="flex items-center justify-center space-x-1 bg-green-600 min-w-[75px] h-7 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs shadow"
          >
            Apply
          </button>

          {/* STATUS */}

          <div>
            <label className="text-xs font-medium block mb-1">Status</label>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="px-2 py-1 text-xs border rounded w-[150px]"
            >
              <option value="">All</option>

              <option value="unassigned">Unassigned</option>

              <option value="picked up">Picked Up</option>

              <option value="started">Started</option>

              <option value="inprogress">In Progress</option>

              <option value="food is ready">Food Ready</option>

              <option value="acknowledged">Acknowledged</option>

              <option value="successful">Successful</option>

              <option value="failed">Failed</option>

              <option value="declined">Declined</option>

              <option value="cancelled">Cancelled</option>

              <option value="arrived">Arrived</option>

              <option value="assigned">Assigned</option>
            </select>
          </div>

          {/* SEARCH */}

          <div className="flex-1">
            <label className="text-xs font-medium block mb-1">Search</label>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={search}
                onChange={(event) => {
                  const value = event.target.value;

                  setSearch(value);

                  if (!value.trim()) {
                    fetchTasks(fromDate, toDate, setTasks, {
                      pageSize: pageInfo.pageSize,

                      nextToken: "",

                      previousToken: "",

                      setPageInfo,
                    });
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleTaskTabGlobalSearch();
                  }
                }}
                placeholder="Search task, order, customer, driver..."
                className="min-w-[200px] px-2 py-1 border rounded text-xs"
              />

              <button
                type="button"
                onClick={handleTaskTabGlobalSearch}
                disabled={globalSearchLoading}
                className="flex items-center space-x-1 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs shadow disabled:opacity-60"
              >
                {globalSearchLoading ? (
                  <>
                    <Loader size={14} className="animate-spin" />

                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Search size={14} />

                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* REFRESH */}

          <button
            type="button"
            onClick={() => {
              setSearch("");

              fetchTasks(fromDate, toDate, setTasks, {
                pageSize: pageInfo.pageSize,

                nextToken: "",

                previousToken: "",

                setPageInfo,
              });
            }}
            className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs shadow"
          >
            <RefreshCcw size={14} />

            <span>Refresh</span>
          </button>

          {/* EXPORT */}

          <button
            type="button"
            className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs shadow"
            onClick={() =>
              exportAnyJsonToExcel({
                data: tasks,
                filePrefix: "TaskExport",
                sheetName: "TasksList",
              })
            }
          >
            <FileDown size={14} />

            <span>Export</span>
          </button>
        </div>
      </div>

      {/* =================================================
          DATA
      ================================================= */}

      {loading || globalSearchLoading ? (
        <LoadingScreen />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="h-full overflow-auto">
              {current.length > 0 ? (
                <table className="min-w-full">
                  <thead className="sticky top-0 z-20 bg-orange-400 text-white border-b border-gray-200">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                        T.I.D
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                        Task / Order
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                        Customer / Contact
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                        Route / Schedule
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                        Driver / Delivery
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                        Status
                      </th>

                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 bg-white">
                    {current.map((task) => {
                      const itemCount =
                        task?.delivery_details?.items?.length || 0;

                      const isCritical = Number(task?.critical || 0) >= 2;

                      return (
                        <tr
                          key={task.pk_task_id}
                          className={`align-top transition-colors duration-200 ${
                            isCritical
                              ? "bg-red-50 hover:bg-red-100"
                              : "hover:bg-orange-50/40"
                          }`}
                        >
                          {/* TASK ID */}

                          <td className="px-4 py-4 text-center">
                            <div className="mx-auto flex h-8 w-8 items-center justify-center text-xs font-bold text-orange-700">
                              {task.pk_task_id}
                            </div>
                          </td>

                          {/* TASK */}

                          <td className="px-4 py-3">
                            <div className="min-w-[220px] space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900 leading-none">
                                  {task.task_description || "N/A"}
                                </p>

                                {Number(task?.critical || 0) >= 2 && (
                                  <InfoBadge color="red">
                                    <span className="inline-flex items-center gap-1">
                                      <ShieldAlert size={12} />
                                      Critical {task?.critical}
                                    </span>
                                  </InfoBadge>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-1.5">
                                <InfoBadge color="blue">
                                  <span className="inline-flex items-center gap-1">
                                    <Hash size={11} />
                                    Task #{task.pk_task_id || "N/A"}
                                  </span>
                                </InfoBadge>

                                <InfoBadge color="green">
                                  Order #{task.order_id || "N/A"}
                                </InfoBadge>

                                <InfoBadge color="purple">
                                  Daily #{task.merchant_order_id || "N/A"}
                                </InfoBadge>
                              </div>

                              <div className="text-xs text-gray-500">
                                <span className="font-medium text-gray-600">
                                  Type:
                                </span>{" "}
                                {task.trans_type || "N/A"}
                                {task.shop_code
                                  ? ` • Shop: ${task.shop_code}`
                                  : ""}
                              </div>
                            </div>
                          </td>

                          {/* CUSTOMER */}

                          <td className="px-4 py-3">
                            <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                              <div className="flex items-center gap-2 min-w-0">
                                <User
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span className="font-medium text-gray-900 truncate">
                                  {task.customer_name || "N/A"}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 min-w-0">
                                <Phone
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span className="truncate">
                                  {task.contact_number || "N/A"}
                                </span>
                              </div>

                              <div className="flex items-start gap-2 min-w-0">
                                <MapPin
                                  size={13}
                                  className="text-gray-400 shrink-0 mt-0.5"
                                />

                                <span className="leading-5 text-gray-600">
                                  {getShortAddress(task.delivery_address)}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* ROUTE */}

                          <td className="px-4 py-3">
                            <div className="min-w-[260px] space-y-2 text-xs text-gray-700">
                              <div className="flex items-center gap-2">
                                <CalendarDays
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span>
                                  <span className="font-medium text-gray-600">
                                    Delivery:
                                  </span>{" "}
                                  {formatDateTime(task.delivery_date)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Clock
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span>
                                  <span className="font-medium text-gray-600">
                                    Assigned limit:
                                  </span>{" "}
                                  {formatDateTime(task.assigned_limit_time)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Route
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span>
                                  {task.taskDistance || "N/A"}

                                  {task.taskEstimatedTime
                                    ? ` • ETA ${task.taskEstimatedTime}`
                                    : ""}
                                </span>
                              </div>

                              <div className="text-gray-500 leading-5">
                                <span className="font-medium text-gray-600">
                                  Pickup:
                                </span>{" "}
                                {getShortAddress(task.pickup_address, 42)}
                              </div>
                            </div>
                          </td>

                          {/* DRIVER */}

                          <td className="px-4 py-3">
                            <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <InfoBadge color="orange">
                                  Driver: {task.driver_name || "Unassigned"}
                                </InfoBadge>

                                {task.driver_mobile && (
                                  <InfoBadge color="gray">
                                    {task.driver_mobile}
                                  </InfoBadge>
                                )}

                                {task.team_id && (
                                  <InfoBadge color="blue">
                                    Team #{task.team_id}
                                  </InfoBadge>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <Package
                                  size={13}
                                  className="text-gray-400 shrink-0"
                                />

                                <span>
                                  {itemCount} item
                                  {itemCount !== 1 ? "s" : ""}
                                </span>
                              </div>

                              <div className="text-gray-500">
                                Merchant status:{" "}
                                <span className="font-medium text-gray-700">
                                  {task.merchant_order_status || "N/A"}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* STATUS */}

                          <td className="px-4 py-3">
                            <div className="flex min-w-[170px] flex-col gap-2">
                              <div>{renderStatusBadge(task.status)}</div>

                              <div>
                                {renderStatusBadge(task.merchant_order_status)}
                              </div>
                            </div>
                          </td>

                          {/* ACTION */}

                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50"
                                title="Show Details"
                                onClick={() => {
                                  setSelectedTask(task);

                                  setAssignmentLogs([]);

                                  setStatusChangeLogs([]);

                                  setShowLogs(false);
                                }}
                              >
                                <Eye size={16} />
                              </button>

                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-green-600 transition hover:bg-green-50"
                                onClick={() => {}}
                                title="Docs / Notes"
                              >
                                <FileText size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex min-h-[220px] items-center justify-center text-sm font-medium text-gray-500">
                  No tasks available for the selected filters.
                </div>
              )}
            </div>
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          <div className="shrink-0 mt-4">
            <div className="flex items-center justify-between gap-3 mt-4">
              <div className="text-sm text-gray-600">
                Page {pageInfo.pageNumber} • Total {pageInfo.totalCount}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handlePreviousPage}
                  disabled={!pageInfo.previousToken || loading}
                  className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <button
                  type="button"
                  onClick={handleNextPage}
                  disabled={!pageInfo.nextToken || loading}
                  className="px-4 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            <TablePagination
              currentPage={currentPage}
              totalCount={filtered.length}
              pageSize={perPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TaskTab;
